import { useParams } from "react-router";

import { SATOSPortal } from "@/data-adapter/SATOSPortal";
import { LiveQueryKeys } from "@/features/live";
import { SavedLiveTagRequest } from "@/features/live/model/satos-live/settings/SavedTagValue";
import { ChecklistQueryKeys } from "@/features/procedures/api/checklist.queries";
import { ProcedureNodeStepComment } from "@/features/procedures/model/procedure/ProcedureNodeStepComment";
import {
	DeleteProcedureChecklistRequest,
	ProcedureNodeSteps,
	QueryUpsertProcedureNodeSteps,
} from "@/features/procedures/model/procedure/ProcedureNodeSteps";
import { Checklist } from "@/features/procedures/model/types/checklist";
import {
	ProcedureNodeStep,
	StepStatus,
	UpdateStepStatusRequest,
} from "@/features/procedures/model/types/checklist";
import {
	AddLiveTagToStepRequest,
	CreateChecklistRequest,
	CreateChecklistStepRequest,
	DeleteChecklistStepRequest,
	RemoveLiveTagFromStepRequest,
	RemoveManualMeasurementPointFromStepRequest,
	UpdateChecklistStepRequest,
} from "@/features/procedures/model/types/manualChecklist";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ApplicationQueryKeys } from "@/service/ApplicationQueries";
import { ProcedureQueryKeys } from "@/service/ProcedureQueries";
import { RouteParams } from "@/service/Utilities";

export const useCreateChecklistStep = () => {
	const queryClient = useQueryClient();
	const { schemeId } = useParams<RouteParams>();
	const checklistId = +schemeId!;

	const { mutateAsync } = useMutation({
		mutationFn: (request: CreateChecklistStepRequest) =>
			SATOSPortal.CreateChecklistStep(request),
		onMutate: () => {
			queryClient.cancelQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			});
			const previousValues = queryClient.getQueryData([
				ChecklistQueryKeys.checklist,
				checklistId,
			]);

			return previousValues;
		},
		onError: (_, variables, previousValues) => {
			queryClient.setQueryData(
				[ChecklistQueryKeys.checklist, checklistId],
				previousValues,
			);

			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			});
		},
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			}),
	});

	return mutateAsync;
};

export const useUpdateChecklistStep = () => {
	const queryClient = useQueryClient();
	const { schemeId, id } = useParams<RouteParams>();
	const procedureId = +id!;
	const checklistId = +schemeId!;

	const { mutateAsync, isPending } = useMutation({
		mutationFn: (request: UpdateChecklistStepRequest) =>
			SATOSPortal.UpdateChecklistStep(request),
		onMutate: () => {
			queryClient.cancelQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			});
			const previousValues = queryClient.getQueryData([
				ChecklistQueryKeys.checklist,
				checklistId,
			]);

			return previousValues;
		},
		onError: (_, variables, previousValues) => {
			queryClient.setQueryData(
				[ChecklistQueryKeys.checklist, +variables.ChecklistId],
				previousValues,
			);

			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, +variables.ChecklistId],
			});
		},
		onSuccess: (request, variables) => {
			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, +variables.ChecklistId],
			});
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, procedureId],
			});
		},
	});

	return { mutateAsync, isPending };
};

export const useDeleteChecklistStep = () => {
	const queryClient = useQueryClient();
	const { schemeId } = useParams<RouteParams>();
	const checklistId = +schemeId!;

	const { mutateAsync } = useMutation({
		mutationFn: (request: DeleteChecklistStepRequest) =>
			SATOSPortal.DeleteChecklistStep(request),

		onMutate: (stepId) => {
			queryClient.cancelQueries({
				queryKey: [ChecklistQueryKeys.checklist, stepId],
			});
			const previousValues = queryClient.getQueryData([
				ChecklistQueryKeys.checklist,
				stepId,
			]);

			return previousValues;
		},
		onError: (_, variables, previousValues) => {
			queryClient.setQueryData(
				[ChecklistQueryKeys.checklist, variables],
				previousValues,
			);

			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, variables],
			});
		},
		onSuccess: (stepId) => {
			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, stepId],
			});
			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			});
		},
	});

	return mutateAsync;
};

export const useAddLiveTagToStep = () => {
	const queryClient = useQueryClient();
	const { schemeId } = useParams<RouteParams>();
	const checklistId = +schemeId!;

	const { mutateAsync } = useMutation({
		mutationFn: (request: AddLiveTagToStepRequest) =>
			SATOSPortal.AddLiveTagToStep(request),
		onMutate: () => {
			queryClient.cancelQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			});
			const previousValues = queryClient.getQueryData([
				ChecklistQueryKeys.checklist,
				checklistId,
			]);

			return previousValues;
		},
		onError: (_, variables, previousValues) => {
			queryClient.setQueryData(
				[ChecklistQueryKeys.checklist, checklistId],
				previousValues,
			);

			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			});
		},
	});
	return mutateAsync;
};

export const useRemoveLiveTagFromStep = () => {
	const queryClient = useQueryClient();
	const { schemeId } = useParams<RouteParams>();
	const checklistId = +schemeId!;

	const { mutateAsync } = useMutation({
		mutationFn: (request: RemoveLiveTagFromStepRequest) =>
			SATOSPortal.RemoveLiveTagFromStep(request),
		onMutate: () => {
			queryClient.cancelQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			});
			const previousValues = queryClient.getQueryData([
				ChecklistQueryKeys.checklist,
				checklistId,
			]);

			return previousValues;
		},
		onError: (_, variables, previousValues) => {
			queryClient.setQueryData(
				[ChecklistQueryKeys.checklist, checklistId],
				previousValues,
			);

			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, checklistId],
			});
		},
	});
	return mutateAsync;
};

export const useRemoveManualMeasurementPointFromStep = () => {
	const queryClient = useQueryClient();

	const { mutateAsync } = useMutation({
		mutationFn: (request: RemoveManualMeasurementPointFromStepRequest) =>
			SATOSPortal.RemoveManualMeasurementPointFromStep(request),
		onSuccess: (_, request) => {
			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, request.ChecklistId],
			});
		},
	});

	return mutateAsync;
};

export const useCreateChecklist = () => {
	const queryClient = useQueryClient();
	const { mutateAsync } = useMutation({
		mutationFn: (request: CreateChecklistRequest) =>
			SATOSPortal.CreateChecklist(request),

		onMutate: () => {
			queryClient.cancelQueries({ queryKey: [ProcedureQueryKeys.procedures] });
			const previousValues = queryClient.getQueryData([
				ProcedureQueryKeys.procedures,
			]);

			return { previousValues };
		},
		onError: (err, _, context) => {
			if (context?.previousValues) {
				queryClient.setQueryData(
					[ProcedureQueryKeys.procedures],
					context.previousValues,
				);
			}
		},
		onSettled: (_data, _error, variables) => {
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, variables.ProcedureId],
			});
		},
	});

	return mutateAsync;
};

export const UpsertProcedureNodeSteps = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: QueryUpsertProcedureNodeSteps) =>
			SATOSPortal.UpsertChecklist(request),
		onMutate: (request: QueryUpsertProcedureNodeSteps) => {
			queryClient.cancelQueries({
				queryKey: [
					ProcedureQueryKeys.procedureDetails,
					request.ProcedureId,
					ProcedureQueryKeys.details,
					request.ChecklistId,
				],
			});
			queryClient.cancelQueries({
				queryKey: [ChecklistQueryKeys.checklist, request.ChecklistId],
			});

			const previousValue: ProcedureNodeSteps | undefined =
				queryClient.getQueryData([
					ChecklistQueryKeys.checklist,
					request.ChecklistId,
				]);

			const mergedSteps = previousValue?.Steps.map((step) => {
				return {
					...step,
					...request.NodeSteps.find((mut) => mut.Id === step.Id),
				};
			});

			queryClient.setQueryData(
				[
					ProcedureQueryKeys.procedureDetails,
					request.ProcedureId,
					ProcedureQueryKeys.details,
					request.ChecklistId,
				],
				{
					...previousValue,
					Name: request.Name,
					Ingress: request.Ingress,
					Steps: mergedSteps,
				},
			);

			return previousValue;
		},
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(
				[
					ProcedureQueryKeys.procedureDetails,
					variables.ProcedureId,
					ProcedureQueryKeys.details,
					variables.ChecklistId,
				],
				previousValue,
			);
		},
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedureDetails,
						variables.ProcedureId,
					],
					refetchActive: true,
					refetchInactive: true,
				});
				queryClient.invalidateQueries({
					queryKey: [ChecklistQueryKeys.checklist, variables.ChecklistId],
				});
			}
		},
		onSuccess: (data, variables, previousValues) => {
			const merged: ProcedureNodeSteps = {
				...data,
				Steps: data.NodeSteps,
				ActiveListenerTags: previousValues?.ActiveListenerTags ?? [],
				ListenerTags: previousValues?.ListenerTags ?? [],
				IsModified: false,
			};

			queryClient.setQueryData<ProcedureNodeSteps>(
				[
					ProcedureQueryKeys.procedureDetails,
					variables.ProcedureId,
					ProcedureQueryKeys.details,
					variables.ChecklistId,
				],
				merged,
			);
		},
	});

	return mutation.mutateAsync;
};

export const useDeleteChecklist = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: DeleteProcedureChecklistRequest) =>
			SATOSPortal.DeleteProcedureChecklist(request),
		onMutate: (request: DeleteProcedureChecklistRequest) => {
			queryClient.cancelQueries({
				queryKey: [
					ProcedureQueryKeys.procedureDetails,
					request.ProcedureId,
					ProcedureQueryKeys.details,
					request.SchemeId,
				],
			});
			const previousValue: ProcedureNodeSteps | undefined =
				queryClient.getQueryData([
					ProcedureQueryKeys.procedureDetails,
					request.ProcedureId,
				]);

			return previousValue;
		},
		onSettled: (data, error, request) => {
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedure, request.ProcedureId],
			});
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, request.ProcedureId],
			});
			queryClient.invalidateQueries({
				queryKey: [
					ProcedureQueryKeys.procedureDetails,
					request.ProcedureId,
					+request.SchemeId,
				],
			});
			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, +request.SchemeId],
			});
		},
	});

	return mutation.mutateAsync;
};

export const useUpdateStepStatus = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (request: UpdateStepStatusRequest) =>
			SATOSPortal.UpdateStepStatus(request),
		onMutate: (request) => {
			// Optimistic update status
			const statusesQueryKey = [
				ChecklistQueryKeys.checklist,
				request.SchemeId,
				ChecklistQueryKeys.status,
			];
			queryClient.cancelQueries({ queryKey: statusesQueryKey });

			// Snapshot the previous status state
			const statusesPreviousState =
				queryClient.getQueryData<StepStatus[]>(statusesQueryKey);

			// Optimistically update statuses (skip when oldData undefined—statuses not loaded yet)
			queryClient.setQueryData(statusesQueryKey, (oldData: any) => {
				if (!Array.isArray(oldData)) return oldData;
				return oldData.map((x: any) => {
					if (x.Id === request.StepId) {
						return {
							...x,
							Status: request.Status,
							IsSkipped: request.IsSkipped,
							IsOverridden: request.IsOverridden,
						};
					}
					return x;
				});
			});

			const checklistQueryKey = [
				ChecklistQueryKeys.checklist,
				request.SchemeId,
				ChecklistQueryKeys.comments,
			];

			let checklistPreviousState: Checklist | undefined;

			if (request.ArchiveType === "Comment") {
				checklistPreviousState =
					queryClient.getQueryData<Checklist>(checklistQueryKey);

				queryClient.setQueryData(
					checklistQueryKey,
					(oldData: Record<number, ProcedureNodeStepComment>) => {
						const newComment = {
							ArchiveType: "Comment",
							IsTask: false,
							Id: 9999999999999999,
							ChecklistId: request.SchemeId,
							StepId: request.StepId,
							ArchiveReference: 0,
							Date: request.TaskDate,
							NumberOfFields: 0,
							Status: 0,
							UserName: request.UserName,
							Value: request.Value,
						};

						if (request?.StepId && Array.isArray(oldData[request?.StepId])) {
							oldData[request.StepId].push(newComment);
						} else {
							oldData[request.StepId] = [newComment];
						}

						return oldData;
					},
				);
			}

			return {
				previousState: { statusesPreviousState, checklistPreviousState },
			};
		},
		// Rollback on error
		onError: (err, request, previousState) => {
			if (previousState) {
				queryClient.setQueryData(
					[
						ChecklistQueryKeys.checklist,
						request.SchemeId,
						ChecklistQueryKeys.status,
					],
					previousState.statusesPreviousState,
				);

				if (request.ArchiveType === "Comment") {
					queryClient.setQueryData(
						[
							ChecklistQueryKeys.checklist,
							request.SchemeId,
							ChecklistQueryKeys.comments,
						],
						previousState.checklistPreviousState,
					);
				}
			}
		},
		// Invalidate and refetch after mutation
		onSuccess: (response, request) => {
			queryClient.setQueryData(
				[
					ChecklistQueryKeys.checklist,
					request.SchemeId,
					ChecklistQueryKeys.status,
				],
				() => response,
			);
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, request.ProcedureId],
			});
			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, request.SchemeId],
			});
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedures, ProcedureQueryKeys.active],
			});

			if (request.ArchiveType === "Comment") {
				queryClient.invalidateQueries({
					queryKey: [
						ChecklistQueryKeys.checklist,
						request.SchemeId,
						ChecklistQueryKeys.comments,
					],
				});
			}
		},
	});

	return mutation.mutateAsync;
};

export const useSaveChecklistTitle = () => {
	const queryClient = useQueryClient();
	const { schemeId } = useParams<RouteParams>();
	const checklistId = +schemeId!;

	const mutation = useMutation({
		mutationFn: (Name: string) => {
			// Optionally perform additional operations like saving to the server
			return Promise.resolve(Name); // Simulate a mutation
		},
		// Update the cache directly on mutation
		onMutate: (Name: string) => {
			const oldData = queryClient.getQueryData([
				ChecklistQueryKeys.checklist,
				checklistId,
			]);

			queryClient.setQueryData([ChecklistQueryKeys.checklist, checklistId], {
				...(oldData || {}),
				Name,
			});
		},
	});

	return mutation;
};

export const PatchManualChecklist = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: ({
			request,
			checklistId,
			procedureId,
		}: {
			request: any;
			checklistId: number;
			procedureId: number;
		}) => SATOSPortal.UpdateChecklist(request, checklistId, procedureId),
		onMutate: ({ checklistId }) => {
			queryClient.cancelQueries({
				queryKey: [ChecklistQueryKeys.checklist, +checklistId],
			});
			const previousValues = queryClient.getQueryData([
				ChecklistQueryKeys.checklist,
				+checklistId,
			]);

			return previousValues;
		},
		onError: (_, variables, previousValues) => {
			queryClient.setQueryData(
				[ChecklistQueryKeys.checklist, +variables.checklistId],
				previousValues,
			);

			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, +variables.checklistId],
			});
		},
		onSuccess: (request, variables) => {
			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, +variables.checklistId],
			});
		},
	});

	return mutation;
};

export const useSaveChecklistDescription = () => {
	const queryClient = useQueryClient();
	const { schemeId } = useParams<RouteParams>();
	const checklistId = +schemeId!;

	const mutation = useMutation({
		mutationFn: (Ingress: string) => {
			// Optionally perform additional operations like saving to the server
			return Promise.resolve(Ingress); // Simulate a mutation
		},
		// Update the cache directly on mutation
		onMutate: (Ingress: string) => {
			const oldData = queryClient.getQueryData([
				ChecklistQueryKeys.checklist,
				checklistId,
			]);

			queryClient.setQueryData([ChecklistQueryKeys.checklist, checklistId], {
				...(oldData || {}),
				Ingress,
			});
		},
	});

	return mutation;
};

export const useUpdateChecklistStepPreview = () => {
	const queryClient = useQueryClient();
	const { schemeId } = useParams<RouteParams>();
	const checklistId = +schemeId;

	const mutation = useMutation({
		mutationFn: ({
			index,
			step,
		}: {
			index: number;
			step: ProcedureNodeStep;
		}) => {
			// Optionally perform additional operations like saving to the server
			return Promise.resolve({ index, step }); // Simulate a mutation
		},
		// Update the cache directly on mutation
		onMutate: ({ index, step }: { index: number; step: ProcedureNodeStep }) => {
			const oldData: Checklist | undefined = queryClient.getQueryData([
				ChecklistQueryKeys.checklist,
				checklistId,
			]);

			if (oldData) {
				const newSteps = [...(oldData.Steps || [])];
				const existingStep = oldData.Steps?.find((s) => s.Id === step.Id);
				if (existingStep) {
					const existingStepIndex = oldData.Steps?.indexOf(existingStep);
					if (existingStepIndex !== undefined) {
						newSteps[existingStepIndex] = step;
					}
				} else if (index >= 0 && index < newSteps.length) {
					newSteps[index] = step;
				}

				queryClient.setQueryData([ChecklistQueryKeys.checklist, checklistId], {
					...oldData,
					Steps: newSteps,
				});
			}
		},
	});

	return mutation;
};

export const useMutateSaveLiveTagValue = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (request: SavedLiveTagRequest) =>
			SATOSPortal.SaveLiveTagValue(request),
		onMutate: () => {
			void queryClient.invalidateQueries({
				queryKey: [
					LiveQueryKeys.live,
					LiveQueryKeys.saveTagValue,
					ApplicationQueryKeys.user,
				],
			});

			const previousSettings = queryClient.getQueryData([
				LiveQueryKeys.live,
				LiveQueryKeys.saveTagValue,
				ApplicationQueryKeys.user,
			]);

			return previousSettings;
		},
		onError: (_, newSettings, previousSettings) => {
			queryClient.setQueryData(
				[
					LiveQueryKeys.live,
					LiveQueryKeys.saveTagValue,
					ApplicationQueryKeys.user,
				],
				previousSettings,
			);
		},
		onSettled: (variables) => {
			void queryClient.invalidateQueries({
				queryKey: [
					LiveQueryKeys.live,
					LiveQueryKeys.saveTagValue,
					ApplicationQueryKeys.user,
				],
			});
			void queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, +variables?.SchemeId],
			});
		},
	});

	return mutation.mutateAsync;
};


export type Step = {
	title: string;
	description: string;
	type: string;
	ParentId?: number;
	manualMeasurement?: {
		tag: string;
		value: string;
		unit: string;
	};
	SATOSLiveListener?: {
		tag: string;
		value: number;
	};
};
